import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import useAutoOpenAdd from "../common/useAutoOpenAdd";
import TruncatedText from "../common/TruncatedText";
import { formatShortDate } from "../chiefComplaints/dateHelpers";
import LabInvestigationService from "./LabInvestigationService";
import DigitiseModal from "./DigitiseModal";
import TrendModal from "./TrendModal";
import {
  FlaskIcon,
  HeaderSearchIcon,
  ToolbarPlusIcon,
  UploadIcon,
} from "./labIcons";
import { Sparkle } from "../controls";
import { LI_V2_EDIT, LI_V2_REFRESH, LI_V2_DIGITISE } from "./events";
// Anthro writes proxy into api-lab-investigation-v2 (ADR-0001 Phase 4),
// so an Anthro-side save can change what this section reads. Listen for
// the Anthro refresh event to keep the two views in sync.
import { ANTH_V2_REFRESH } from "../anthropometric/events";
import { readingTag } from "./data";
import useIsMobile from "mobile/useIsMobile";
import MOBILE from "mobile/mobileConfig";
import MobileLabList from "mobile/components/MobileLabList";

/**
 * Lab investigations — the CONTENT card, rebuilt to Figma 3041:11675
 * ("Card · Lab investigations (populated)", 832×842):
 *
 *   header 50   title + "· N entries" · search 300×34 · "+ Add investigation"
 *   thead 42    INVESTIGATION 226 | LATEST TEST DATE 146 | VALUE 100 |
 *               UNITS 130 | REF. RANGE 108 | ANALYSIS 103  (≈4px gutters)
 *   rows 64     flagged rows tinted #fef6f5, value red with an arrow,
 *               ref. range blue, "View trend" orange on EVERY row
 *   panel band  44px #f1f2f6 — name + "N entries" pill + "N flagged" pill
 *
 * Tests with no panel render as plain rows ABOVE the panel groups (the board
 * puts "Heart rate (resting)" first), not in a fake "Individual
 * investigations" group.
 *
 * Add/Edit/Delete moved to the EDITOR RAIL (LabInvestigationEditor) — the
 * board draws no delete column, and the 6-column grid has no room for one.
 * A row click hands the record to the rail via LI_V2_EDIT.
 */

// Flagged decision — a test's LATEST reading in {low, high, abnormal}
// counts as flagged. Historical readings that were once out-of-range
// but have since normalised do NOT count (only latest matters).
const isFlaggedFlag = (flag) => flag === "low" || flag === "high" || flag === "abnormal";

const groupByPanel = (list) => {
  const loose = [];
  const buckets = new Map();
  for (const inv of list || []) {
    const key = inv.test && (inv.test.ltCode || inv.test.panel);
    if (!key) {
      loose.push(inv);
      continue;
    }
    if (!buckets.has(key)) {
      buckets.set(key, {
        key,
        panel: (inv.test && inv.test.panel) || "",
        entries: [],
        latestAt: 0,
      });
    }
    const b = buckets.get(key);
    b.entries.push(inv);
    const t = new Date(inv.testDate).getTime();
    if (!isNaN(t) && t > b.latestAt) b.latestAt = t;
  }
  const groups = Array.from(buckets.values()).map((b) => {
    // Collapse to distinct tests; count + flag on the latest per test only.
    const tests = groupByTest(b.entries);
    return {
      ...b,
      testCount: tests.length,
      flaggedCount: tests.filter((tg) => isFlaggedFlag(tg.latest && tg.latest.flag)).length,
    };
  });
  groups.sort((a, b) => b.latestAt - a.latestAt);
  loose.sort((a, b) => new Date(b.testDate).getTime() - new Date(a.testDate).getTime());
  return { loose, groups };
};

// Same-date tiebreaker for sorting Investigation rows — the more
// recently created / updated entry wins so a fresh add on today's date
// beats an older row with the same testDate. Missing fields fall back
// to 0 so undefined never accidentally sorts as "biggest".
const rankOf = (inv) => {
  const u = inv && inv.updatedAt && new Date(inv.updatedAt).getTime();
  const a = inv && inv.addedAt && new Date(inv.addedAt).getTime();
  return Math.max(!isNaN(u) ? u : 0, !isNaN(a) ? a : 0);
};

// Collapse a group's entries to the latest row per test; older entries feed
// the trend. Sort is desc by testDate with `rankOf` as the tiebreaker so
// two entries on the same date are ordered by their creation/update
// timestamp — the reviewer's LATEST addition wins.
const groupByTest = (entries) => {
  const buckets = new Map();
  for (const inv of entries) {
    const key = (inv.test && inv.test.sltCode) || (inv.test && inv.test.name) || "";
    if (!key) continue;
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(inv);
  }
  const byLatestFirst = (a, b) => {
    const dt = new Date(b.testDate).getTime() - new Date(a.testDate).getTime();
    if (dt !== 0) return dt;
    return rankOf(b) - rankOf(a);
  };
  const testGroups = Array.from(buckets.entries()).map(([key, list]) => {
    list.sort(byLatestFirst);
    return { key, entries: list, latest: list[0] };
  });
  testGroups.sort((a, b) => byLatestFirst(a.latest, b.latest));
  return testGroups;
};

// Range cell display — the user-picked Range (None/Normal/Abnormal/High/
// Medium/Low) from the Digitise-manually flow lives on the entry's
// `flag` field. Title-case for display; "—" when unset or "unknown".
const RANGE_DISPLAY = {
  none:     "None",
  normal:   "Normal",
  abnormal: "Abnormal",
  high:     "High",
  medium:   "Medium",
  low:      "Low",
};
const formatRangeCell = (flag) => {
  if (!flag || flag === "unknown") return "—";
  return RANGE_DISPLAY[flag] || flag;
};

/** One data row — shared by loose tests and panel children. */
const Row = ({ tg, onTrend, shortcutEnabled }) => {
  const latest = tg.latest;
  // The value shown is the latest reading, so its tag says which reading it is.
  const tag = readingTag(latest.test);
  const flagged = latest.flag === "low" || latest.flag === "high" || latest.flag === "abnormal";
  const captured = latest._captured;
  // Click routing:
  //   • Transcript-mode captured rows (peach highlight) — emit
  //     LI_V2_EDIT with `{ captured }`; CapturedSections' listener
  //     routes to the reedit card stack, so the row re-opens as a
  //     DigitiseManuallyCard seeded from its payload.
  //   • Standalone-mode patient rows — emit LI_V2_EDIT with
  //     `{ __shortcutReading, investigation }`; LabInvestigationEditor
  //     opens DigitiseManuallyCard in "shortcut" mode with panel /
  //     sub-test / LOINC pre-picked so the reviewer can add a new
  //     reading against the SAME test without re-searching the
  //     catalog. Not offered in transcript mode.
  //   • Transcript-mode patient rows — no handler; the section spec
  //     locks pre-existing data during a call ("provider can only add
  //     or edit new entries discussed on the call").
  const canShortcut = !captured && shortcutEnabled;
  const isClickable = !!captured || canShortcut;
  const onRowClick = isClickable
    ? (e) => {
        // Guard against clicks that originate from a control inside the
        // row (View trend button today) — those shouldn't double as a
        // row-open trigger.
        if (e && e.target && e.target.closest && e.target.closest("button")) return;
        if (captured) {
          EventService.emit(LI_V2_EDIT, { captured });
        } else {
          EventService.emit(LI_V2_EDIT, {
            __shortcutReading: true,
            investigation: latest,
          });
        }
      }
    : undefined;
  return (
    <div
      className={`li3-row${flagged ? " is-flagged" : ""}${captured ? " is-captured is-clickable" : (canShortcut ? " is-clickable" : "")}`}
      role="row"
      onClick={onRowClick}
      style={isClickable ? { cursor: "pointer" } : undefined}
    >
      <span className="li3-cell li3-c-name" role="cell">
        {latest._capturedIsAi ? <span className="cc-spark" aria-label="AI captured" title="AI captured"><Sparkle aria-hidden /></span> : null}
        {latest.test.name}
        {tag ? <span className="li-tag">{tag}</span> : null}
      </span>
      <span className="li3-cell li3-c-date" role="cell">{formatShortDate(latest.testDate)}</span>
      <span className="li3-cell li3-c-result" role="cell">
        {latest.flag === "high" && <span className="li3-arr" aria-label="High">↑</span>}
        {latest.flag === "low" && <span className="li3-arr" aria-label="Low">↓</span>}
        {/* Long free-form values ("Detected in Sample: 12.5 mg/L …")
            clamp to a single line via TruncatedText. Show more chip
            drops beneath and opens the full text in a portalled
            dialog — same contract as the Description column. */}
        <TruncatedText
          text={latest.value != null ? String(latest.value) : ""}
          title="Value"
        />
      </span>
      <span className="li3-cell li3-c-units" role="cell">{latest.test.unit || "—"}</span>
      <span className="li3-cell li3-c-range" role="cell">{formatRangeCell(latest.flag)}</span>
      <span className="li3-cell li3-c-desc" role="cell">
        <TruncatedText text={latest.description} />
      </span>
      <span className="li3-cell li3-c-analysis" role="cell">
        <button
          type="button"
          className="li3-trend"
          onClick={() => onTrend(tg)}
        >
          View trend
        </button>
      </span>
    </div>
  );
};
Row.propTypes = {
  tg: PropTypes.object.isRequired,
  onTrend: PropTypes.func.isRequired,
  /** Standalone-mode only. When true, patient rows become clickable and
   *  open the shortcut DigitiseManuallyCard for adding a new reading. */
  shortcutEnabled: PropTypes.bool,
};
Row.defaultProps = { shortcutEnabled: false };

const Thead = () => (
  <div className="li3-thead" role="row">
    <span role="columnheader">Investigation</span>
    <span role="columnheader">Latest test date</span>
    <span role="columnheader">Value</span>
    <span role="columnheader">Units</span>
    <span role="columnheader">Range</span>
    <span role="columnheader">Description</span>
    <span role="columnheader">Analysis</span>
  </div>
);

// Build a synthetic Investigation-shaped row from a staged "add"
// captured item so it can slot into the same panel-grouping + filter
// pipeline as Lab v2 rows. Mirrors CC/Dx/Med/Hosp.
const stagedAddToLabRow = (item) => {
  const p = (item && item.payload) || {};
  // Reviewer-picked Range rides on the captured payload as `flag`
  // (see labInvestigationBodyAdapters.cardCommitToCapturedItems).
  // Feeding it through to the synthetic row gives the Row component
  // both the Range column label AND its `is-flagged` background
  // treatment (High / Low / Abnormal). Falls back to "unknown" for
  // legacy payloads without the flag field.
  const flag = String(p.flag || "").toLowerCase() || "unknown";
  return {
    investigationId: `staged-${item.id}`,
    test: {
      sltCode:   "",
      ltCode:    "",
      loincCode: p.loinc_code || "",
      name:      p.test_name || "(untitled)",
      panel:     p.investigation_panel || p.test_name || "(untitled)",
      unit:      p.result_units || "",
      refRange:  { text: p.ref_range || "" },
      // Qualifier tag ride-along so the Row component's readingTag()
      // finds it and paints the "· TagName" pill on the section
      // table straight after the reviewer commits — no need to wait
      // for the server round-trip.
      tag:       p.tag || "",
    },
    testDate:     p.test_date || null,
    value:        p.result_value || "",
    flag,
    labName:      "",
    source:       "manual",
    description:  p.description || "",
    statusHistory: [],
    _captured:     item,
    _capturedIsAi: (item.confidence || 0) > 0,
    _stagedAdd:    true,
  };
};

const LabInvestigations = ({ patient, capturedItems, appointmentId, autoOpenAdd, onAutoOpenAdd }) => {
  const patientId = patient && (patient.id || patient.patientID);

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [trendTarget, setTrendTarget] = useState(null);
  const [digitiseOpen, setDigitiseOpen] = useState(false);
  const [openPanels, setOpenPanels] = useState({});
  const [search, setSearch] = useState("");

  /* MOBILE ONLY. The card list lives in src/mobile/components/MobileLabList;
     this line, the LI_V2_DIGITISE listener and the branch before the final
     return are its whole footprint here. Called unconditionally, as a hook
     must be. */
  const mobile = useIsMobile() && MOBILE.labCards;

  const load = useCallback(() => {
    if (!patientId) return;
    setLoading(true);
    setError(null);
    const svc = new LabInvestigationService();
    svc.list(
      patientId,
      (res) => {
        setDoc(res || { investigations: [], version: 0 });
        setLoading(false);
      },
      (err) => {
        setError((err && err.message) || "Failed to load lab investigations");
        setLoading(false);
      }
    );
  }, [patientId]);

  useEffect(() => {
    load();
    EventService.addListener(LI_V2_REFRESH, load);
    EventService.addListener(ANTH_V2_REFRESH, load);
    return () => {
      EventService.removeListener(LI_V2_REFRESH, load);
      EventService.removeListener(ANTH_V2_REFRESH, load);
    };
  }, [load]);

  const rowsRaw = (doc && doc.investigations) || [];

  // Transcript-mode overlay. Wave A first-cut merges only staged ADDS —
  // each captured item becomes one Investigation row (Lab's 1:1 mapping
  // per sub-test).
  const inTranscriptMode = !!(capturedItems && capturedItems.length > 0);
  const rows = useMemo(() => {
    if (!inTranscriptMode) return rowsRaw;
    const stagedAdds = [];
    for (const it of capturedItems) {
      if (!it) continue;
      if (it.state !== "added_in_call" && it.state !== "edited_in_call") continue;
      if (it.operation === "add") stagedAdds.push(stagedAddToLabRow(it));
    }
    return [...rowsRaw, ...stagedAdds];
  }, [rowsRaw, capturedItems, inTranscriptMode]);

  // ONE search box (board: "Search by name or date…") — matches the test
  // name, the panel, the latest reading's tag, and the row's date in either
  // its displayed ("13 May 2026") or ISO form.
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((inv) => {
      const name = (inv.test && inv.test.name) || "";
      const panel = (inv.test && inv.test.panel) || "";
      const tag = readingTag(inv.test);
      const iso = String(inv.testDate || "").slice(0, 10);
      const shown = formatShortDate(inv.testDate) || "";
      return (
        name.toLowerCase().includes(needle) ||
        panel.toLowerCase().includes(needle) ||
        tag.toLowerCase().includes(needle) ||
        iso.includes(needle) ||
        shown.toLowerCase().includes(needle)
      );
    });
  }, [rows, search]);

  const { loose, groups } = useMemo(() => groupByPanel(filtered), [filtered]);

  const isOpen = (map, key) => map[key] !== false; // default open
  const togglePanel = (key) =>
    setOpenPanels((prev) => ({ ...prev, [key]: !isOpen(prev, key) }));

  // "+ Add investigation" and the empty-state CTA both open the Digitise
  // modal directly now — the intermediate chooser (with Add manually) is
  // gone; manual entry lives inside the Digitise modal via "Digitise
  // manually" instead.
  //
  // Transcript-mode override: `capturedItems` being non-null signals
  // the AI-transcript context. In that mode the "+ Add investigation"
  // affordance emits an empty LI_V2_EDIT so CapturedSections'
  // openManual listener pushes a fresh DigitiseManuallyCard into the
  // right-column rail (matches CC / Dx / Med add-flow parity). The
  // centred DigitiseModal stays for the standalone patient page.
  const isTranscriptMode = capturedItems != null;
  const openDigitise = () => {
    if (isTranscriptMode) {
      EventService.emit(LI_V2_EDIT, {});
      return;
    }
    setDigitiseOpen(true);
  };
  useAutoOpenAdd({ autoOpenAdd, onAutoOpenAdd, loading, error, doc, open: openDigitise });

  /* The way in from outside the card. On mobile the add button is pinned below
     the list rather than drawn in its header, so it cannot call openDigitise —
     it emits this instead, and the modal opened is the same one. */
  useEffect(() => {
    const open = () => setDigitiseOpen(true);
    EventService.addListener(LI_V2_DIGITISE, open);
    return () => EventService.removeListener(LI_V2_DIGITISE, open);
  }, []);

  // Store just the identity in trendTarget; the derivation below runs on
  // every render so the modal picks up mutations (correction, mark
  // incorrect, add, edit) via the LI_V2_REFRESH → load → doc update chain
  // WITHOUT the caller having to close + reopen the modal.
  const openTrend = (tg) => {
    setTrendTarget({ key: tg.key });
  };

  // Flatten each matching investigation's StatusHistory into a single
  // stream of trend entries — every edit/add snapshot becomes its own row
  // in the audit + point on the chart. Falls back to the investigation's
  // current snapshot when history is missing (legacy data / older records).
  const deriveTrendData = (key) => {
    const matching = rows.filter((inv) => {
      if (!inv.test) return false;
      return inv.test.sltCode === key || (!inv.test.sltCode && inv.test.name === key);
    });
    if (!matching.length) return null;

    const entries = [];
    const version = (doc && doc.version) || 0;
    for (const inv of matching) {
      const history = Array.isArray(inv.statusHistory) ? inv.statusHistory : [];
      if (history.length === 0) {
        // Legacy row with no audit — synthesize a neutral row so the
        // correction flag still renders (backend will 400 on save
        // because there's no index to target, matching intent: legacy
        // rows without history can't be corrected until they get one).
        entries.push({
          ...inv,
          _origInvestigationId: inv.investigationId,
          _historyIndex: -1,
          _docVersion: version,
        });
        continue;
      }
      history.forEach((h, hIdx) => {
        entries.push({
          // Synthetic id combining the investigation + history index — React key
          // stability across renders without piggybacking on recordedAt.
          investigationId: `${inv.investigationId}-${hIdx}`,
          // Real IDs / version — used by the correction flow to target the
          // right audit row on the right document.
          _origInvestigationId: inv.investigationId,
          _historyIndex: hIdx,
          _docVersion: version,
          // Per-audit provenance — prefer the audit entry's own values
          // (each StatusEntry stores its own Source/DocumentID/AppointmentID
          // as of that write). Falls back to the parent's top-level
          // values for legacy rows that predate the per-entry fields.
          documentId: h.documentId != null && h.documentId !== "" ? h.documentId : inv.documentId,
          source: h.source != null && h.source !== "" ? h.source : inv.source,
          appointmentId: h.appointmentId != null && h.appointmentId !== "" ? h.appointmentId : inv.appointmentId,
          testDate: h.testDate,
          value: h.value,
          numericValue: h.numericValue,
          flag: h.flag,
          labName: h.labName || inv.labName,
          description: h.description,
          // Prefer the snapshot's test if it was captured; otherwise use the
          // investigation's current test snapshot so refRange still renders —
          // WITHOUT its tag, which belongs to the latest reading, not this one.
          test: (h.test && h.test.name) ? h.test : { ...inv.test, tag: "" },
          reportedBy: h.reportedBy || inv.updatedBy || inv.addedBy,
          reportedByName: h.reportedByName,
          recordedAt: h.recordedAt,
          event: h.event,
          // Correction / mark-incorrect state on THIS audit entry —
          // powers the flag column + inline dialogs in TrendModal.
          markedIncorrect: h.markedIncorrect,
          markedIncorrectBy: h.markedIncorrectBy,
          markedIncorrectByName: h.markedIncorrectByName,
          markedIncorrectAt: h.markedIncorrectAt,
          correction: h.correction,
        });
      });
    }
    return { test: matching[0].test, entries };
  };

  if (!patientId) {
    return <div className="ph-slot-empty"><p>Waiting for patient…</p></div>;
  }
  if (loading && !doc) {
    return <div className="ph-slot-empty"><p>Loading lab investigations…</p></div>;
  }
  if (error) {
    return (
      <div className="ph-slot-empty">
        <p>{error}</p>
        <button type="button" className="sec-btn sec-btn-ghost" onClick={load}>Retry</button>
      </div>
    );
  }

  const looseTests = groupByTest(loose);
  // Total = distinct tests across loose + every panel. Historical
  // readings for the same test collapse to one entry (only the latest
  // reading counts on the pill), matching the per-panel count rule.
  const distinctTestCount =
    looseTests.length + groups.reduce((n, g) => n + g.testCount, 0);

  /* The two portalled modals, pulled out of the tree so BOTH layouts mount
     them. The mobile card list has no "+ Add investigation" of its own — its
     add button emits LI_V2_DIGITISE — so without this the modal it opens would
     have nowhere to render. Desktop's output is unchanged: same elements, same
     place in the tree. */
  const labOverlays = (
    <>
      {digitiseOpen && (
        <DigitiseModal
          patientId={patientId}
          appointmentId={appointmentId}
          docVersion={(doc && doc.version) || 0}
          onClose={() => setDigitiseOpen(false)}
        />
      )}

      {trendTarget && (() => {
        // Re-derive on every render so the modal reflects the latest
        // `doc` state after LI_V2_REFRESH → load fires (e.g., after a
        // correction / mark-incorrect / add / edit). If the identity no
        // longer resolves (all matching investigations gone), close.
        const data = deriveTrendData(trendTarget.key);
        if (!data) return null;
        return (
          <TrendModal
            patientId={patientId}
            test={data.test}
            entries={data.entries}
            onClose={() => setTrendTarget(null)}
          />
        );
      })()}
    </>
  );

  /* The mobile arrangement: the same filtered, grouped data as cards. After
     the last hook, so hook order is identical on both paths; the desktop tree
     below is unreachable on mobile and unchanged by it. The search box writes
     the section's own `search`, so filtering is still decided here. */
  if (mobile) {
    return (
      <>
        <MobileLabList
          count={distinctTestCount}
          hasRows={rows.length > 0}
          search={search}
          onSearch={setSearch}
          looseTests={looseTests}
          groups={groups}
          isPanelOpen={(key) => isOpen(openPanels, key)}
          onTogglePanel={togglePanel}
          testsOf={(group) => groupByTest(group.entries)}
        />
        {labOverlays}
      </>
    );
  }

  return (
    <div className="sec-card li3-card">
      <header className={`li3-head${rows.length === 0 ? " is-empty" : ""}`}>
        <h2 className="li3-title">Lab investigations</h2>
        <span className="li3-count">
          &middot; {distinctTestCount === 0
            ? "No entries"
            : `${distinctTestCount} ${distinctTestCount === 1 ? "entry" : "entries"}`}
        </span>
        {rows.length > 0 && (
          <>
            <div className="li3-search">
              <span className="li3-search-icon"><HeaderSearchIcon /></span>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or date…"
                aria-label="Search lab investigations"
              />
            </div>
            <button
              type="button"
              className="li3-add"
              onClick={openDigitise}
            >
              <ToolbarPlusIcon /> Add investigation
            </button>
          </>
        )}
      </header>

      {rows.length === 0 ? (
        <div className="li3-table" role="table">
          <Thead />
          <div className="li3-empty">
            <span className="li3-empty-medallion" aria-hidden="true"><FlaskIcon /></span>
            <p className="li3-empty-title">No lab investigations yet</p>
            <p className="li3-empty-hint">
              We haven&rsquo;t recorded any labs for this patient. Add the first
              investigation to start tracking results over time.
            </p>
            {/* Manual entry moved into the Digitise modal (Digitise manually
                button) — this empty state now only offers the Digitise
                entry point. */}
            <div className="li3-method-btns">
              <button type="button" className="li3-btn-digitise" onClick={openDigitise}>
                <UploadIcon /> Digitise report
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="li3-table" role="table">
          <Thead />

          {looseTests.map((tg) => (
            <Row
              key={tg.key}
              tg={tg}
              onTrend={openTrend}
              shortcutEnabled={!isTranscriptMode}
            />
          ))}

          {groups.map((group) => {
            const open = isOpen(openPanels, group.key);
            const tests = groupByTest(group.entries);
            return (
              <React.Fragment key={group.key}>
                <button
                  type="button"
                  className="li3-band"
                  onClick={() => togglePanel(group.key)}
                  aria-expanded={open}
                >
                  <span className="li3-band-name">{group.panel}</span>
                  <span className="li3-pill">
                    {group.testCount} {group.testCount === 1 ? "entry" : "entries"}
                  </span>
                  {group.flaggedCount > 0 && (
                    <span className="li3-pill is-flagged">
                      <span className="li3-pill-dot" aria-hidden="true" />
                      {group.flaggedCount} flagged
                    </span>
                  )}
                </button>
                {open &&
                  tests.map((tg) => (
                    <Row
                      key={tg.key}
                      tg={tg}
                      onTrend={openTrend}
                      shortcutEnabled={!isTranscriptMode}
                    />
                  ))}
              </React.Fragment>
            );
          })}
        </div>
      )}


      {labOverlays}
    </div>
  );
};

LabInvestigations.propTypes = {
  /** Open the add form once the list loads — Create Rx's "+ Add …" (useAutoOpenAdd). */
  autoOpenAdd: PropTypes.bool,
  onAutoOpenAdd: PropTypes.func,
  patient: PropTypes.object,
  /**
   * Transcript-mode overlay. When populated, staged captured items for
   * `lab_investigations` are merged into the panel-grouped table
   * (peach highlight + sparkle for AI-touched rows). Empty / unpassed
   * = standalone behaviour. Wave A first-cut merges only staged ADDS.
   */
  capturedItems: PropTypes.array,
};

LabInvestigations.defaultProps = {
  // Null (not []) is the standalone default — the openDigitise branch
  // uses `capturedItems != null` to detect transcript-mode context.
  // With `[]` as the default, standalone was silently masquerading as
  // "transcript with 0 captures" and firing LI_V2_EDIT (which opens
  // the flat LabInvestigationForm) instead of setDigitiseOpen(true)
  // (which opens the Digitise report chooser).
  capturedItems: null,
};

export default LabInvestigations;
